import { styled, SxProps } from '@mui/material/styles';
import { AvatarSize } from 'features/layout/features/dimensions/types';
import { SourceDetails } from 'features/layout/types';
import FeedDebugControls from 'features/dev-mode/components/FeedDebugControls';
import ScreenshareDebugControls from 'features/dev-mode/components/ScreenshareDebugControls';
import RemoteScreenshare from 'features/streaming/components/media/RemoteScreenshare';
import { TileControlButtons } from 'features/streaming/components/TileControlButtons';
import LocalScreenshare from 'features/streaming/components/media/LocalScreenshare';
import RemoteMedia from 'features/streaming/components/media/RemoteMedia';
import LocalMedia from 'features/streaming/components/media/LocalMedia';
import DebugMedia from 'features/streaming/components/media/DebugMedia';
import ExternalStream from 'features/streaming/components/media/ExternalStream';
import { UserBadges } from 'features/streaming/components/UserBadges';
import React, { ComponentPropsWithoutRef } from 'react';
import { useAppSelector } from 'store/hooks';
import { selectUserById } from 'features/users/usersSlice';
import { useSystemSession } from 'hooks/useSystemSession';
import { hoverParentStyles } from 'features/layout/useHoverParent';
import { Theme } from '@mui/material';

const Root = styled('div')(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  justifyContent: 'center',
  padding: theme.spacing(4),
  containerType: 'size',
  '&:focus': {
    outline: 'none',
  },
  ...hoverParentStyles,
}));

const mediaByKind = {
  debug: DebugMedia,
  local: LocalMedia,
  'screenshare-local': LocalScreenshare,
  remote: RemoteMedia,
  'screenshare-remote': RemoteScreenshare,
  external: ExternalStream,
  custom: () => null,
};

type StreamDisplayProps = {
  source: SourceDetails;
  avatarSize?: AvatarSize;
  sx?: SxProps<Theme>;
} & ComponentPropsWithoutRef<'div'>;

const StreamDisplay = React.memo(({ source, avatarSize, ...props }: StreamDisplayProps) => {
  const isSystemSession = useSystemSession();

  const user = useAppSelector((state) => selectUserById(state, source.userId));
  if (!user && source.kind !== 'external') {
    return null;
  }

  const isUserTile = source.kind === 'local' || source.kind === 'remote';

  const Media = mediaByKind[source.kind];

  return (
    <Root tabIndex={0} {...props}>
      <Media source={source} avatarSize={avatarSize} />
      {isUserTile && <UserBadges user={user} />}
      {!isSystemSession && <TileControlButtons source={source} />}
      {source.kind === 'screenshare-local' || source.kind === 'screenshare-remote' ? (
        <ScreenshareDebugControls source={source} />
      ) : (
        <FeedDebugControls source={source} />
      )}
    </Root>
  );
});

export default StreamDisplay;
